import { Component, type ErrorInfo, type ReactNode } from "react";

type ErrorBoundaryProps = {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
};

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (typeof console !== "undefined") {
      console.error("Unhandled error caught by ErrorBoundary:", error, errorInfo);
    }
  }

  resetError = () => {
    this.props.onReset?.();
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="app">
          <main className="shell" style={{ marginTop: "2rem" }}>
            <div className="flash flash--error" role="alert">
              <strong>Something went wrong</strong>
              <p style={{ margin: "0.5rem 0 0" }}>
                An unexpected error occurred while displaying this page.
              </p>
              {this.state.error?.message ? (
                <pre
                  style={{
                    margin: "0.75rem 0 0",
                    padding: "0.5rem",
                    background: "rgba(0,0,0,0.05)",
                    fontSize: "0.85rem",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {this.state.error.message}
                </pre>
              ) : null}
            </div>
            <div className="actions" style={{ marginTop: "1rem" }}>
              <button
                type="button"
                onClick={() => {
                  this.resetError();
                  if (typeof window !== "undefined") {
                    window.location.reload();
                  }
                }}
              >
                Reload page
              </button>
              <a className="btn ghost" href="/">
                Return home
              </a>
            </div>
          </main>
        </div>
      );
    }

    return this.props.children;
  }
}
